import type { TFormFieldDict } from '../types/field'
import type { IForm } from '../types/form'
import { isRef, ref  } from 'vue'
import { isFunction } from '@/utils/is'
import type { IInputOption, IInputProps } from '../input/types'
import { toBoolean } from '@/utils/boolean'
import type { FormV2UserState } from '../types/state'
import { toString } from '@/utils/formatting'
import { isFormFunction } from '../types/attributes'
import type { StringIndexable } from '@/types/object'

export const getFieldOptions = <T extends { [key: string]: any } = {}>(
  fieldDef: IForm<any>,
  state?: T
): IInputOption[] => {
  if (!fieldDef.$options) return []
  return (
    (isFunction(fieldDef.$options.items)
      ? fieldDef.$options.items({ state })
      : fieldDef.$options.items) || []
  )
}

const createFieldRef = <T extends { [key: string]: any } = {}, ContextType extends (StringIndexable | undefined) = StringIndexable>(
  key: string,
  fieldDef: IForm<any>,
  state: FormV2UserState<T>,
  top: { state: FormV2UserState<T>; schema: IForm<T, ContextType> },
  path: string[] = []
) => {

  const getContext = () => ({
    state: top.state,
    fields: {},
    path: key
  });

  const fieldName = fieldDef.$name || key

  let fieldValue = state[key] ?? fieldDef.$value ?? ''

  if (isFunction(fieldDef.$value)) {
    fieldValue = fieldDef.$value(getContext())
  }

  const hidden = toBoolean(
    isFunction(fieldDef.$hidden) ? fieldDef.$hidden(getContext()) : fieldDef.$hidden
  )
  const locked = toBoolean(
    isFunction(fieldDef.$locked) ? fieldDef.$locked(getContext()) : fieldDef.$locked
  )
  const injectedProps = isFormFunction(fieldDef.$injectedProps, '$injectedProps')
    ? fieldDef.$injectedProps(getContext())
    : fieldDef.$injectedProps;

  const display = isFormFunction(fieldDef.$display, '$display') ? fieldDef.$display(getContext()) : fieldDef.$display;

  const lockedReason = String(
    isFunction(fieldDef.$lockedReason) ? fieldDef.$lockedReason(getContext()) : fieldDef.$lockedReason
  )
  const placeholder = toString(
    isRef(fieldDef.$placeholder)
      ? fieldDef.$placeholder.value
      : isFunction(fieldDef.$placeholder)
        ? fieldDef.$placeholder(getContext())
        : fieldDef.$placeholder
  )

  const fieldPath = [...path, fieldName].join('.')

  const fieldRef = ref<IInputProps>({
    type: fieldDef.$type || 'text',
    path: fieldPath,
    schema: fieldDef,
    //schema:  proxy<IForm>(fieldDef, {
    //  get: (target, key) => {
    //    return target[key];
    //  }
    //}),
    validators: fieldDef.$validators,
    componentType: fieldDef.$componentType,
    value: ['bool', 'boolean', 'switch'].includes(fieldDef.$type || '')
      ? toBoolean(fieldValue ?? '')
      : fieldValue ?? '',
    label: fieldDef.$label || '',
    labelClass: fieldDef.$labelClass,
    labelPlacement: fieldDef.$labelPlacement,
    subLabel: fieldDef.$subLabel || '',
    dependants: fieldDef.$dependants || [],
    numberPrecision: fieldDef.$numberPrecision,
    clearFieldsIfNotTrue: fieldDef.$clearFieldsIfNotTrue || [],
    locked: locked,
    lockedReason: lockedReason,
    display: display,
    name: fieldName,
    subtitle: fieldDef.$subtitle,
    hidden: hidden,
    touched: fieldDef.$touched,
    options: fieldDef.$options
      ? {
          name: fieldDef.$options.name || fieldName,
          items: getFieldOptions(fieldDef, top.state),
          maxVisibleItems: fieldDef.$options.maxVisibleItems,
          selectedItemComponent: fieldDef.$options.selectedItemComponent,
          selectedStyle: fieldDef.$options.selectedStyle,
          selectedClass: fieldDef.$options.selectedClass,
          css: fieldDef.$options.css,
          search: fieldDef.$options.search,
          add: fieldDef.$options.add,
          defaultOption: fieldDef.$options.defaultOption,
          selectedItem: fieldDef.$options.selectedItem,
        }
      : undefined,
    title: fieldDef.$title,
    disabled: locked,
    before: fieldDef.$before,
    after: fieldDef.$after,
    tooltip: fieldDef.$tooltip,
    bubbleTip: fieldDef.$bubbleTip,
    subtitleClass: fieldDef.$subtitleClass,
    numberTweening: fieldDef.$numberTweening,
    customComponent: fieldDef.$customComponent,
    inlineLabel: fieldDef.$inlineLabel,
    variant: fieldDef.$variant,
    icon: fieldDef.$icon,
    injectedProps: injectedProps,
    placeholder: placeholder,
    errorProps: fieldDef.$errorProps,
    range: fieldDef.$range,
    errors: [],
    placeholderIcon: fieldDef.$placeholderIcon,
    placeholderClass: fieldDef.$placeholderClass,
    wrapper: fieldDef.$wrapper,
    changeReportedCallback: fieldDef.$changeReportedCallback,
    wrapperClass: fieldDef.$wrapperClass,
    css: fieldDef.$css,
    placement: fieldDef.$placement,
    required: false // TODO
  })

  return fieldRef
}

export const createFieldsDict = <T extends { [key: string]: any } = {}, ContextType extends (StringIndexable | undefined) = StringIndexable>(
  state: FormV2UserState<T>,
  schema: IForm<T, ContextType>,
  maxDepth: number = -1,
  top: { state: FormV2UserState<T>; schema: IForm<T, ContextType> } = { state, schema },
  path: string[] = []
): TFormFieldDict<T> => {
  return Object.assign(
    {},
    ...Object.entries(schema).map(([key, value]) => {
      if (typeof value !== 'object') return {}
      if (!value) return {}
      //if (typeof state[key] === 'object') {
      //  return {[key]: createFieldsDict(state[key] || {}, value[key] || {}, maxDepth)};
      //}

      const objKeys = Object.keys(value as {})
      const fieldDef = value as IForm<any>
      const fieldName = fieldDef.$name || key
      const fieldRef = createFieldRef<T, ContextType>(key, fieldDef, state, top, path)

      if (objKeys.includes('$value') || objKeys.includes('$type')) {
        return {
          [fieldName]: fieldRef
        }
      }
      return {
        [fieldName]: {
          $ref: fieldRef,
          ...createFieldsDict<T>(
            state[key] || {},
            value[key as keyof typeof value] || value,
            maxDepth,
            (top as any) || { state, schema },
            [...path, fieldName]
          )
        }
      }
    })
  )
}
